import {
  OrganizationAnalyticsService,
  type SchoolAliasPublic,
  type SchoolAliasSuggestion,
} from "@/client"
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from "@/components/ui/drawer"
import {
  Badge,
  Box,
  Button,
  HStack,
  Input,
  Stack,
  Text,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useState } from "react"

interface SchoolAliasPanelProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  orgId: string
}

export function SchoolAliasPanel({
  isOpen,
  onOpenChange,
  orgId,
}: SchoolAliasPanelProps) {
  const queryClient = useQueryClient()
  const [dismissedSuggestions, setDismissedSuggestions] = useState<string[]>([])
  const [canonicalName, setCanonicalName] = useState("")
  const [aliasName, setAliasName] = useState("")

  const { data: aliases } = useQuery({
    queryKey: ["schoolAliases", orgId],
    queryFn: () => OrganizationAnalyticsService.readSchoolAliases({ orgId }),
    enabled: isOpen,
  })

  const { data: suggestions } = useQuery({
    queryKey: ["schoolAliasSuggestions", orgId],
    queryFn: () =>
      OrganizationAnalyticsService.readSchoolAliasSuggestions({ orgId }),
    enabled: isOpen,
  })

  const invalidateAll = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["schoolAnalytics", orgId] }),
      queryClient.invalidateQueries({ queryKey: ["schoolAliases", orgId] }),
      queryClient.invalidateQueries({
        queryKey: ["schoolAliasSuggestions", orgId],
      }),
    ])
  }

  const createAlias = useMutation({
    mutationFn: (payload: { canonical_name: string; alias: string }) =>
      OrganizationAnalyticsService.createSchoolAlias({
        orgId,
        requestBody: payload,
      }),
    onSuccess: async () => {
      setCanonicalName("")
      setAliasName("")
      await invalidateAll()
    },
  })

  const deleteAlias = useMutation({
    mutationFn: (aliasId: string) =>
      OrganizationAnalyticsService.deleteSchoolAlias({
        orgId,
        aliasId,
      }),
    onSuccess: invalidateAll,
  })

  const visibleSuggestions =
    suggestions?.suggestions.filter(
      (suggestion) => !dismissedSuggestions.includes(suggestion.alias),
    ) ?? []

  function renderAliasRow(alias: SchoolAliasPublic) {
    return (
      <Box key={alias.id} borderWidth="1px" borderRadius="md" px={3} py={3}>
        <HStack justify="space-between" align="start" gap={3}>
          <Stack gap={0} flex="1" minW={0}>
            <Text fontWeight="semibold" textWrap="pretty">
              {alias.canonical_name}
            </Text>
            <Text color="fg.muted" fontSize="sm">
              {alias.alias}
            </Text>
          </Stack>
          <Button
            size="sm"
            variant="outline"
            flexShrink={0}
            onClick={() => deleteAlias.mutate(alias.id)}
          >
            Remove
          </Button>
        </HStack>
      </Box>
    )
  }

  function renderSuggestionRow(suggestion: SchoolAliasSuggestion) {
    return (
      <Box
        key={`${suggestion.alias}-${suggestion.suggested_canonical_name}`}
        borderWidth="1px"
        borderRadius="md"
        px={3}
        py={3}
      >
        <Stack gap={3}>
          <HStack justify="space-between" align="start" gap={3}>
            <Stack gap={0} flex="1" minW={0}>
              <Text fontWeight="semibold" textWrap="pretty">
                {suggestion.alias}
              </Text>
              <Text color="fg.muted" fontSize="sm" textWrap="pretty">
                {suggestion.suggested_canonical_name}
              </Text>
            </Stack>
            <Badge flexShrink={0}>
              {Math.round(suggestion.confidence * 100)}%
            </Badge>
          </HStack>
          <HStack align="stretch" wrap="wrap">
            <Button
              size="sm"
              minW={{ base: "100%", sm: "180px" }}
              whiteSpace="normal"
              h="auto"
              py={2}
              onClick={() =>
                createAlias.mutate({
                  canonical_name: suggestion.suggested_canonical_name,
                  alias: suggestion.alias,
                })
              }
              aria-label={`Approve ${suggestion.alias}`}
            >
              Approve suggestion
            </Button>
            <Button
              size="sm"
              variant="outline"
              flexShrink={0}
              onClick={() =>
                setDismissedSuggestions((current) => [
                  ...current,
                  suggestion.alias,
                ])
              }
            >
              Dismiss
            </Button>
          </HStack>
        </Stack>
      </Box>
    )
  }

  return (
    <DrawerRoot open={isOpen} onOpenChange={({ open }) => onOpenChange(open)}>
      <DrawerBackdrop />
      <DrawerContent maxW={{ base: "100vw", md: "560px" }}>
        <DrawerHeader>
          <DrawerTitle>School aliases</DrawerTitle>
          <DrawerCloseTrigger />
        </DrawerHeader>
        <DrawerBody>
          <Stack gap={6}>
            <Stack gap={3}>
              <Text fontWeight="semibold">Create alias</Text>
              <Input
                placeholder="Canonical school name"
                value={canonicalName}
                onChange={(event) => setCanonicalName(event.target.value)}
              />
              <Input
                placeholder="Alias"
                value={aliasName}
                onChange={(event) => setAliasName(event.target.value)}
              />
              <Button
                alignSelf="start"
                disabled={!canonicalName.trim() || !aliasName.trim()}
                onClick={() =>
                  createAlias.mutate({
                    canonical_name: canonicalName,
                    alias: aliasName,
                  })
                }
              >
                Save alias
              </Button>
            </Stack>

            <Stack gap={3}>
              <Text fontWeight="semibold">Approved aliases</Text>
              {(aliases?.data ?? []).map(renderAliasRow)}
              {!aliases?.count ? (
                <Text color="fg.muted">No aliases yet.</Text>
              ) : null}
            </Stack>

            <Stack gap={3}>
              <Text fontWeight="semibold">Suggestions</Text>
              {visibleSuggestions.map(renderSuggestionRow)}
              {visibleSuggestions.length === 0 ? (
                <Text color="fg.muted">No suggestions available.</Text>
              ) : null}
            </Stack>
          </Stack>
        </DrawerBody>
      </DrawerContent>
    </DrawerRoot>
  )
}
